import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, Stack, Typography, Button, IconButton, Chip, Alert,
  LinearProgress, Tooltip, CircularProgress, Grid, Grow, Fade, Divider,
  Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  useMediaQuery,
} from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import {
  CloudUpload as CloudIcon,
  UploadFile as FileIcon,
  Folder as FolderIcon,
  Delete as DeleteIcon,
  CheckCircle as DoneIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as ErrorIcon,
  ExpandMore as ExpandIcon,
  PlayArrow as RunIcon,
  Insights as AnalyzeIcon,
  Replay as RedoIcon,
  Description as SheetIcon,
  Close as CloseIcon,
  RocketLaunch as RocketIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import swal from '../utils/swal';

const ACCEPTED = ['.xls', '.xlsx', '.xml'];

const floatAnim = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-4px); }
`;

// TeacherImportDialog — modal-wrapped bulk score-sheet importer.
// Same backend endpoint and validation as the staff Import Score
// Sheets page. The dialog form is slimmer (no full-page hero, no
// step strip) so it sits cleanly inside the teacher portal.
//
// Backend enforces teacher-scope on /score-sheet/import: any sheet
// whose (cla, sub) isn't on the caller's teacher_assignment list
// gets rejected automatically with a friendly error.
export default function TeacherImportDialog({ open, onClose, onCommitted }) {
  const theme = useTheme();
  const importFullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [files, setFiles]                 = useState([]);
  const [dragOver, setDragOver]           = useState(false);
  const [busy, setBusy]                   = useState(false);
  const [progress, setProgress]           = useState(0);
  const [analysis, setAnalysis]           = useState(null);
  const [committed, setCommitted]         = useState(null);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Reset on every (re-)open so a fresh import starts clean.
  useEffect(() => {
    if (open) {
      setFiles([]); setAnalysis(null); setCommitted(null);
      setProgress(0); setBusy(false);
    }
  }, [open]);

  // ── file queue helpers ──────────────────────────────────────
  const addFiles = useCallback((list) => {
    if (!list || list.length === 0) return;
    const next = [];
    let skipped = 0;
    for (const f of list) {
      const ext = ('.' + f.name.split('.').pop()).toLowerCase();
      if (!ACCEPTED.includes(ext)) { skipped += 1; continue; }
      next.push({
        id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        file: f, name: f.name, size: f.size,
      });
    }
    if (next.length === 0) {
      swal.warning('Only .xls / .xlsx / .xml files are accepted');
      return;
    }
    if (skipped > 0) swal.info(`Added ${next.length}, skipped ${skipped} unsupported.`);
    setFiles(prev => [...prev, ...next]);
    setAnalysis(null); setCommitted(null);
  }, []);

  const removeFile = useCallback((id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
    setAnalysis(null); setCommitted(null);
  }, []);

  // Drop on the dialog body
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  };
  const browse = () => fileInputRef.current?.click();

  // ── upload + analyze / commit ───────────────────────────────
  const post = useCallback(async ({ commit, overwrite }) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f.file, f.name);
    fd.append('commit',    commit    ? 'true' : 'false');
    fd.append('overwrite', overwrite ? 'true' : 'false');
    setProgress(0);
    const { data, status } = await API.post(
      '/examination/score-sheet/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (!e.total) return;
          setProgress(Math.round((e.loaded / e.total) * 100));
        },
        validateStatus: (s) => s < 500,
      }
    );
    setProgress(100);
    return { data, status };
  }, [files]);

  const analyze = async () => {
    if (files.length === 0) return swal.warning('Add at least one file');
    setBusy('analyzing');
    setAnalysis(null); setCommitted(null);
    try {
      const { data } = await post({ commit: false, overwrite: false });
      setAnalysis(data);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not analyze files'));
    } finally {
      setBusy(false);
    }
  };

  const importNow = async (overwrite = false) => {
    if (!analysis) return swal.warning('Analyze first');
    if (analysis.hasErrors) {
      return swal.warning('Some files have errors — fix or remove them first');
    }
    if (analysis.hasConflict && !overwrite) {
      setConfirmOverwriteOpen(true); return;
    }
    setConfirmOverwriteOpen(false);
    setBusy('committing');
    setCommitted(null);
    try {
      const { data, status } = await post({ commit: true, overwrite });
      if (status === 409) {
        setAnalysis(data);
        setConfirmOverwriteOpen(true);
        return;
      }
      if (data?.ok) {
        setCommitted(data);
        swal.success(`Imported ${data.savedRows} row(s) across ${data.totalSheets} sheet(s)`);
        onCommitted?.(data);
      } else {
        swal.error(data?.message || 'Import failed');
        setAnalysis(data);
      }
    } catch (e) {
      swal.error(swal.pickError(e, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  // ── derived ─────────────────────────────────────────────────
  const totals = useMemo(() => {
    if (!analysis) return null;
    let sheets = 0, ok = 0, error = 0, conflict = 0, students = 0;
    for (const f of analysis.files || []) {
      for (const s of f.sheets || []) {
        sheets += 1;
        if (!s.ok) error += 1;
        if (s.conflict) conflict += 1;
        if (s.ok && !s.conflict) ok += 1;
        students += (s.students?.length || 0);
      }
    }
    return { sheets, ok, error, conflict, students };
  }, [analysis]);

  return (
    <Dialog open={open} onClose={() => !busy && onClose?.()}
      maxWidth="md" fullWidth
      // Full-screen on a phone: the import flow has a drop zone, a
      // per-sheet review list and a progress panel, none of which fit
      // in a windowed dialog on a handset. Rounded corners are dropped
      // there too — they look broken edge-to-edge.
      fullScreen={importFullScreen}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, overflow: 'hidden' } }}>
      {/* Gradient header */}
      <Box sx={{
        px: { xs: 2, md: 3 }, py: 2,
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        color: '#fff',
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.22)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CloudIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 17, md: 20 }, lineHeight: 1.15 }}>
            Import your filled score sheets
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Drop Excel files — class, term and exam type read automatically
          </Typography>
        </Box>
        <IconButton onClick={() => !busy && onClose?.()} disabled={!!busy}
          sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ bgcolor: '#f4f6fb', p: { xs: 2, md: 2.5 } }}
        onDragEnter={onDragOver}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}>
        {/* Drop zone */}
        <Card sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3, textAlign: 'center',
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'primary.light',
          bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.06) : 'rgba(255,255,255,0.9)',
          transition: 'all 160ms ease',
        }}>
          <Box sx={{
            width: 64, height: 64, mx: 'auto', mb: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: '#fff',
            boxShadow: `0 10px 22px -10px ${alpha(theme.palette.primary.main, 0.5)}`,
            animation: `${floatAnim} 3s infinite ease-in-out`,
          }}>
            <CloudIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography fontWeight={800} sx={{ fontSize: 16, mb: 0.4 }}>
            Drop Excel files here
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.4 }}>
            .xls · .xlsx · .xml — only Raw Score Sheets the system exported
          </Typography>
          <Button variant="contained" size="medium" onClick={browse}
            startIcon={<FolderIcon />}
            sx={{
              textTransform: 'none', fontWeight: 800, borderRadius: 99,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              boxShadow: `0 8px 18px -8px ${alpha(theme.palette.primary.main, 0.55)}`,
            }}>
            Browse files
          </Button>
          <input type="file" hidden multiple
            accept={ACCEPTED.join(',')}
            ref={fileInputRef}
            onChange={(e) => addFiles(e.target.files)} />
        </Card>

        {/* Queue */}
        {files.length > 0 && (
          <Card sx={{ mt: 1.6, p: 1.6, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center"
              spacing={1.2} sx={{ mb: 1.2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                <Chip color="primary" size="small"
                  label={`${files.length} file${files.length === 1 ? '' : 's'}`}
                  sx={{ fontWeight: 800 }} />
                <Typography variant="caption" color="text.secondary">
                  {humanSize(files.reduce((s, f) => s + f.size, 0))}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined"
                  startIcon={<AnalyzeIcon />} onClick={analyze}
                  disabled={!!busy}
                  sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 99 }}>
                  {busy === 'analyzing' ? 'Analyzing…' : (analysis ? 'Re-analyze' : 'Analyze')}
                </Button>
                <Button size="small" variant="contained"
                  startIcon={<RunIcon />}
                  onClick={() => importNow(false)}
                  disabled={!analysis || !!busy || analysis.hasErrors}
                  sx={{
                    textTransform: 'none', fontWeight: 800, borderRadius: 99,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  }}>
                  {busy === 'committing' ? 'Importing…' : 'Import'}
                </Button>
              </Stack>
            </Stack>

            {!!busy && (
              <Box sx={{ mb: 1 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {busy === 'analyzing' ? 'Uploading & analyzing…' : 'Uploading & saving…'}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main' }}>
                    {progress}%
                  </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={progress}
                  sx={{
                    height: 7, borderRadius: 99,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 99,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    },
                  }} />
              </Box>
            )}

            <Grid container spacing={1}>
              {files.map((f, i) => (
                <Grid item xs={12} sm={6} key={f.id}>
                  <Grow in timeout={150 + i * 50}>
                    <Stack direction="row" alignItems="center" spacing={1}
                      sx={{
                        px: 1.2, py: 0.9, borderRadius: 1.5,
                        border: '1px solid', borderColor: 'divider', bgcolor: '#fff',
                      }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: 1.2,
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                        color: 'success.dark',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <SheetIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontWeight: 700, fontSize: 13, lineHeight: 1.2,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{f.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {humanSize(f.size)}
                        </Typography>
                      </Box>
                      <IconButton size="small" color="error"
                        onClick={() => removeFile(f.id)} disabled={!!busy}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          </Card>
        )}

        {/* Analysis */}
        {analysis && totals && (
          <Card sx={{ mt: 1.6, p: 1.6, borderRadius: 3 }}>
            {committed?.ok && (
              <Alert severity="success" variant="filled"
                icon={<RocketIcon />}
                sx={{ mb: 1.2, borderRadius: 2 }}>
                <Typography fontWeight={800}>
                  Imported {committed.savedRows} student row(s)
                  across {committed.totalSheets} sheet(s).
                </Typography>
              </Alert>
            )}
            {!committed && analysis.hasErrors && (
              <Alert severity="error" sx={{ mb: 1.2, borderRadius: 2 }}>
                Some sheets have validation errors. Fix or remove them
                before importing.
              </Alert>
            )}
            {!committed && !analysis.hasErrors && analysis.hasConflict && (
              <Alert severity="warning" sx={{ mb: 1.2, borderRadius: 2 }}>
                Some sheets are already in the system. Clicking <b>Import</b>
                {' '}will ask before overwriting.
              </Alert>
            )}

            <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', rowGap: 0.6, mb: 1.4 }}>
              <Chip size="small" color="primary"
                label={`${totals.sheets} sheet${totals.sheets === 1 ? '' : 's'}`} />
              {totals.ok > 0 && (
                <Chip size="small" color="success" icon={<DoneIcon sx={{ fontSize: 14 }} />}
                  label={`${totals.ok} ready`} />
              )}
              {totals.conflict > 0 && (
                <Chip size="small" color="warning" icon={<WarnIcon sx={{ fontSize: 14 }} />}
                  label={`${totals.conflict} already imported`} />
              )}
              {totals.error > 0 && (
                <Chip size="small" color="error" icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                  label={`${totals.error} with errors`} />
              )}
              <Chip size="small" variant="outlined"
                label={`${totals.students} student rows`} />
            </Stack>

            {analysis.files.map((file, fi) => (
              <FileReport key={fi} file={file} />
            ))}
          </Card>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.4, bgcolor: 'background.paper',
        borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={() => !busy && onClose?.()} disabled={!!busy}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {committed?.ok ? 'Done' : 'Close'}
        </Button>
      </DialogActions>

      {/* Page-wide drag overlay while files are over the dialog */}
      <Fade in={dragOver} timeout={120}>
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 10,
          bgcolor: alpha(theme.palette.primary.main, 0.18),
          backdropFilter: 'blur(6px)',
          border: `3px dashed ${theme.palette.primary.main}`,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none', borderRadius: 3,
        }}>
          <CloudIcon sx={{ fontSize: 80, color: 'primary.main', mb: 1 }} />
          <Typography sx={{ fontWeight: 900, fontSize: 22, color: 'primary.dark' }}>
            Drop to import
          </Typography>
        </Box>
      </Fade>

      {/* Overwrite confirmation */}
      <Dialog open={confirmOverwriteOpen} onClose={() => !busy && setConfirmOverwriteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800 }}>
          <WarnIcon color="warning" /> Already imported — overwrite?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            <b>{analysis?.files?.flatMap(f => f.sheets.filter(s => s.conflict)).length}</b>
            {' '}sheet(s) already exist in the database. Importing
            will <b>replace</b> the existing scores.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This is permanent — old scores cannot be recovered unless
            you have the previous Excel export.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOverwriteOpen(false)} disabled={!!busy}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cancel
          </Button>
          <Button variant="contained" color="warning"
            onClick={() => importNow(true)} disabled={!!busy}
            startIcon={busy ? <CircularProgress size={16} /> : <RedoIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 99, px: 3 }}>
            {busy === 'committing' ? 'Overwriting…' : 'Yes, overwrite'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

function FileReport({ file }) {
  const theme = useTheme();
  const fileTint = file.ok ? theme.palette.success.main : theme.palette.error.main;
  return (
    <Accordion defaultExpanded={!file.ok || file.sheets?.some(s => s.conflict)}
      elevation={0}
      sx={{
        mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2,
        '&:before': { display: 'none' }, overflow: 'hidden',
      }}>
      <AccordionSummary expandIcon={<ExpandIcon />}
        sx={{ bgcolor: alpha(fileTint, 0.07) }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
          <SheetIcon sx={{ color: fileTint, fontSize: 18 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={800} sx={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontSize: 14,
            }}>
              {file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {file.sheets?.length || 0} sheet(s){file.error ? ` · ${file.error}` : ''}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {file.error && (
          <Alert severity="error" sx={{ mt: 1, mb: 1.2, borderRadius: 1.5 }}>{file.error}</Alert>
        )}
        {(file.sheets || []).map((sheet, si) => <SheetReport key={si} sheet={sheet} />)}
      </AccordionDetails>
    </Accordion>
  );
}

function SheetReport({ sheet }) {
  const theme = useTheme();
  const isError = !sheet.ok;
  const isConflict = sheet.conflict;
  const tint = isError ? theme.palette.error.main
    : isConflict ? theme.palette.warning.main : theme.palette.success.main;
  return (
    <Box sx={{
      mt: 1, border: '1px solid', borderColor: alpha(tint, 0.3), borderRadius: 2,
      bgcolor: 'rgba(255,255,255,0.6)', overflow: 'hidden',
    }}>
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ px: 1.2, py: 0.9, bgcolor: alpha(tint, 0.08) }}>
        {isError ? <ErrorIcon sx={{ color: tint, fontSize: 18 }} />
          : isConflict ? <WarnIcon sx={{ color: tint, fontSize: 18 }} />
          : <DoneIcon sx={{ color: tint, fontSize: 18 }} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} sx={{
            fontSize: 13.5,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {sheet.bucket?.sub || sheet.worksheet_name || '(unknown subject)'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sheet.bucket
              ? `${sheet.bucket.cla || '?'} · ${(sheet.bucket.term || '').replace(/_/g, ' ')} · ${sheet.bucket.state || '?'} · ${sheet.bucket.acc_yer || '?'}`
              : sheet.worksheet_name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.6}>
          <Chip size="small" variant="outlined"
            label={`${sheet.students?.length || 0} students`}
            sx={{ fontWeight: 700, height: 22 }} />
          {isConflict && (
            <Tooltip title={`${sheet.existing_count} row(s) already exist`}>
              <Chip size="small" color="warning"
                label={`exists (${sheet.existing_count})`}
                sx={{ fontWeight: 700, height: 22 }} />
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {sheet.error && (
        <Alert severity="error" sx={{ m: 1.2, borderRadius: 1.5 }}>{sheet.error}</Alert>
      )}

      {sheet.errors && sheet.errors.length > 0 && (
        <Box sx={{ p: 1.2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 12, color: 'error.main', mb: 0.6 }}>
            {sheet.errors.length} issue(s):
          </Typography>
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 800, py: 0.4 }}>Index</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 0.4 }}>Row</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 0.4 }}>Column</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 0.4 }}>Issue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sheet.errors.slice(0, 20).map((e, ei) => (
                  <TableRow key={ei} hover>
                    <TableCell sx={{ py: 0.4, fontFamily: 'monospace', fontSize: 12 }}>{e.ind || '—'}</TableCell>
                    <TableCell sx={{ py: 0.4, fontSize: 12 }}>{e.row || '—'}</TableCell>
                    <TableCell sx={{ py: 0.4, fontSize: 12 }}>{e.column || '—'}</TableCell>
                    <TableCell sx={{ py: 0.4, color: 'error.main', fontWeight: 700, fontSize: 12 }}>
                      {e.issue}
                    </TableCell>
                  </TableRow>
                ))}
                {sheet.errors.length > 20 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ fontStyle: 'italic', py: 0.6, fontSize: 12 }}>
                      and {sheet.errors.length - 20} more…
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

function humanSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
